// Karma Queue Priority Calculator — T3.6 Shipping-V1.
//
// Pattern per Volkan brainstorming Soru 8 ratify: "Karma" queue =
// sistem (PEXIFY engine) önerir + Emin override pins.
//
// Two factors compose the system priority score:
//   - age_factor:    older shipment (waiting longer) raises priority.
//                    24-hour tick = 10 points; capped at 5 days × 10 = 50.
//   - package_factor: multi-piece dispatch = more complex packaging.
//                     (package_count - 1) × 5.
//
// Higher score = higher priority. `now` parameter is injectable so the
// calculator is fully deterministic in tests (Date.now() default at runtime).
//
// applyKarmaSort glues sistem priority to the Emin override layer: pinned
// shipments first (in caller-provided input order so the pin sequence from
// the Zustand store is preserved); remaining unpinned items via sistem sort.

import type { Shipment } from '../../mocks/data/shipments'

const AGE_TICK_MS = 24 * 60 * 60 * 1000
const AGE_CAP_DAYS = 5
const AGE_MULTIPLIER = 10
const PACKAGE_MULTIPLIER = 5

export function calculatePriorityScore(
  shipment: Shipment,
  now: Date = new Date(),
): number {
  const createdAt = new Date(shipment.created_at)
  const ageMs = now.getTime() - createdAt.getTime()
  const ageDays = Math.max(0, ageMs / AGE_TICK_MS)
  const age_factor = Math.min(ageDays, AGE_CAP_DAYS) * AGE_MULTIPLIER

  const package_factor = (shipment.package_count - 1) * PACKAGE_MULTIPLIER

  return age_factor + package_factor
}

export function sortByPrioritySystem(
  shipments: Shipment[],
  now: Date = new Date(),
): Shipment[] {
  return [...shipments].sort(
    (a, b) => calculatePriorityScore(b, now) - calculatePriorityScore(a, now),
  )
}

export function applyKarmaSort(
  shipments: Shipment[],
  pinnedIds: Set<string>,
  now: Date = new Date(),
): Shipment[] {
  const pinned: Shipment[] = []
  const unpinned: Shipment[] = []
  for (const s of shipments) {
    if (pinnedIds.has(s.id)) pinned.push(s)
    else unpinned.push(s)
  }
  return [...pinned, ...sortByPrioritySystem(unpinned, now)]
}
